// Mock data for Research Agent components

export const mockUploadedFile = {
  name: 'b2b_saas_companies.csv',
  size: '2.4 MB',
  rowCount: 1247,
  columnCount: 8,
  columns: ['company_name', 'website', 'industry', 'location', 'employees', 'funding', 'description', 'linkedin'],
  sampleData: [
    {
      company_name: 'TechFlow Solutions',
      website: 'https://techflow.com',
      industry: 'Software',
      location: 'San Francisco, CA',
      employees: '150-200',
      funding: 'Series B',
      description: 'AI-powered workflow automation',
      linkedin: 'https://linkedin.com/company/techflow'
    },
    {
      company_name: 'DataSync Corp',
      website: 'https://datasync.io',
      industry: 'Data Management',
      location: 'Austin, TX',
      employees: '50-100',
      funding: 'Series A',
      description: 'Real-time data synchronization platform',
      linkedin: 'https://linkedin.com/company/datasync'
    },
    {
      company_name: 'CloudBridge Systems',
      website: 'https://cloudbridge.com',
      industry: 'Cloud Infrastructure',
      location: 'Seattle, WA',
      employees: '75-150',
      funding: 'Seed',
      description: 'Multi-cloud management and optimization',
      linkedin: 'https://linkedin.com/company/cloudbridge'
    }
  ]
};

export const mockTemplates = [
  {
    id: 1,
    name: 'B2B SaaS Companies',
    description: 'Standard criteria for identifying B2B software companies',
    category: 'Industry-Specific',
    favorite: true,
    criteria: {
      mustHaves: 'Must be a B2B SaaS company\nMust have raised Series A or later funding\nMust have 50+ employees',
      niceToHaves: 'Preferably based in the US\nIdeally focused on enterprise customers\nBonus if they have API documentation',
      disqualifications: 'Exclude consulting firms\nNo agencies or freelancers\nAvoid pure marketplace businesses'
    },
    enrichmentColumns: [
      {
        id: 1,
        name: 'Employee Count',
        description: 'Current number of employees',
        instructions: 'Find the latest employee count from LinkedIn or company pages',
        dataFormat: 'number',
        useWebSearch: true
      },
      {
        id: 2,
        name: 'Latest Funding',
        description: 'Most recent funding round',
        instructions: 'Extract latest funding information from press releases or investor pages',
        dataFormat: 'currency',
        useWebSearch: true
      }
    ],
    usageCount: 12,
    lastUsed: '2025-09-20'
  },
  {
    id: 2,
    name: 'E-commerce Businesses',
    description: 'Criteria for online retail and e-commerce platforms',
    category: 'Industry-Specific',
    favorite: false,
    criteria: {
      mustHaves: 'Must be an e-commerce business\nMust have online product catalog\nMust process online payments',
      niceToHaves: 'Preferably multi-channel (online + offline)\nIdeally supports international shipping\nBonus if they have mobile app',
      disqualifications: 'Exclude pure B2B marketplaces\nNo dropshipping businesses\nAvoid adult content sites'
    },
    enrichmentColumns: [
      {
        id: 3,
        name: 'Platform Type',
        description: 'E-commerce platform used',
        instructions: 'Identify if using Shopify, WooCommerce, Magento, or custom platform',
        dataFormat: 'text',
        useWebSearch: false
      }
    ],
    usageCount: 8,
    lastUsed: '2025-09-15'
  },
  {
    id: 3,
    name: 'Early Stage Startups',
    description: 'Companies in pre-Series A funding stage',
    category: 'Stage-Specific',
    favorite: true,
    criteria: {
      mustHaves: 'Must be in pre-Series A stage (Seed or earlier)\nMust have fewer than 50 employees\nMust be technology-focused',
      niceToHaves: 'Preferably founded in the last 3 years\nIdeally has some venture backing\nBonus if founder has previous exits',
      disqualifications: 'Exclude lifestyle businesses\nNo traditional brick-and-mortar\nAvoid non-scalable business models'
    },
    enrichmentColumns: [],
    usageCount: 5,
    lastUsed: '2025-09-10'
  },
  {
    id: 4,
    name: 'Sales Prospecting',
    description: 'Companies suitable for B2B sales outreach',
    category: 'Use Case-Specific',
    favorite: false,
    criteria: {
      mustHaves: 'Must be actively hiring\nMust have decision maker contact info\nMust fit our ICP (Ideal Customer Profile)',
      niceToHaves: 'Preferably recently funded\nIdeally has current pain points we solve\nBonus if they are expanding internationally',
      disqualifications: 'Exclude companies with existing competitors\nNo companies in legal trouble\nAvoid companies with hiring freezes'
    },
    enrichmentColumns: [
      {
        id: 4,
        name: 'Decision Maker',
        description: 'CEO or CTO contact information',
        instructions: 'Find LinkedIn profile and email for CEO, CTO, or VP Engineering',
        dataFormat: 'text',
        useWebSearch: true
      }
    ],
    usageCount: 15,
    lastUsed: '2025-09-22'
  }
];

export const mockPreviewData = [
  {
    id: 1,
    website: 'https://techflow.com',
    companyName: 'TechFlow Solutions',
    qualified: true,
    score: 8,
    rating: { label: 'Excellent', color: 'success' },
    reasoning: 'Meets all must-have criteria. B2B SaaS with Series B funding and 150+ employees. Also matches nice-to-haves: US-based and enterprise-focused.',
    enrichedData: {
      'Employee Count': '187',
      'Latest Funding': '$12M Series B',
      'Platform Type': 'Custom',
      'Decision Maker': 'john.doe@techflow.com'
    }
  },
  {
    id: 2,
    website: 'https://consulting-firm.com',
    companyName: 'Business Consulting Group',
    qualified: false,
    score: 0,
    rating: { label: 'Disqualified', color: 'error' },
    reasoning: 'Failed disqualification criteria: This is a consulting firm, which is explicitly excluded.',
    enrichedData: {
      'Employee Count': '45',
      'Latest Funding': 'N/A',
      'Platform Type': 'WordPress',
      'Decision Maker': 'N/A'
    }
  },
  {
    id: 3,
    website: 'https://datasync.io',
    companyName: 'DataSync Corp',
    qualified: true,
    score: 6,
    rating: { label: 'Good', color: 'info' },
    reasoning: 'Meets all must-have criteria. B2B SaaS with Series A funding and 75 employees. Matches some nice-to-haves: US-based.',
    enrichedData: {
      'Employee Count': '78',
      'Latest Funding': '$5M Series A',
      'Platform Type': 'React/Node.js',
      'Decision Maker': 'sarah.smith@datasync.io'
    }
  }
];

export const mockConversationalQuestions = [
  {
    id: 1,
    question: "What type of business are you researching?",
    options: [
      "B2B SaaS Companies",
      "E-commerce Businesses", 
      "Healthcare Companies",
      "Financial Services",
      "Manufacturing",
      "Other"
    ],
    followUp: "This helps us understand your target market and suggest relevant criteria."
  },
  {
    id: 2,
    question: "What stage companies are you interested in?",
    options: [
      "Early Stage (Pre-Series A)",
      "Growth Stage (Series A-C)", 
      "Late Stage (Series D+)",
      "Public Companies",
      "All Stages"
    ],
    followUp: "Company stage often determines their needs, budget, and decision-making process."
  },
  {
    id: 3,
    question: "What's your primary goal for this research?",
    options: [
      "Sales Prospecting",
      "Competitive Analysis",
      "Investment Research",
      "Partnership Opportunities",
      "Market Research",
      "Talent Acquisition"
    ],
    followUp: "Your goal will help us tailor the criteria and suggest relevant data to extract."
  },
  {
    id: 4,
    question: "What company size are you targeting?",
    options: [
      "Startups (1-50 employees)",
      "Small Business (51-200 employees)",
      "Mid-Market (201-1000 employees)",
      "Enterprise (1000+ employees)",
      "Mixed/All Sizes"
    ],
    followUp: "Company size affects budget, decision speed, and organizational complexity."
  },
  {
    id: 5,
    question: "Are there any industries you want to exclude?",
    options: [
      "Adult Content",
      "Gambling/Gaming",
      "Cryptocurrency",
      "Political Organizations",
      "Non-Profits",
      "Government Agencies",
      "None of the above"
    ],
    followUp: "Exclusions help filter out companies that aren't relevant to your goals."
  }
];

export const mockEnrichmentOptions = [
  {
    category: 'Company Information',
    options: [
      {
        name: 'Employee Count',
        description: 'Current number of employees',
        dataFormat: 'number',
        useWebSearch: true,
        credits: 3,
        example: '250'
      },
      {
        name: 'Founded Year',
        description: 'Year the company was established',
        dataFormat: 'date',
        useWebSearch: false,
        credits: 1,
        example: '2018'
      },
      {
        name: 'Company Size',
        description: 'Size category (Startup, SMB, Enterprise)',
        dataFormat: 'text',
        useWebSearch: false,
        credits: 1,
        example: 'Mid-Market'
      },
      {
        name: 'Headquarters Location',
        description: 'Primary business address',
        dataFormat: 'text',
        useWebSearch: false,
        credits: 1,
        example: 'San Francisco, CA'
      }
    ]
  },
  {
    category: 'Financial Information',
    options: [
      {
        name: 'Latest Funding Round',
        description: 'Most recent funding amount and stage',
        dataFormat: 'currency',
        useWebSearch: true,
        credits: 3,
        example: '$10M Series B'
      },
      {
        name: 'Total Funding Raised',
        description: 'Cumulative funding across all rounds',
        dataFormat: 'currency',
        useWebSearch: true,
        credits: 3,
        example: '$25M'
      },
      {
        name: 'Revenue Range',
        description: 'Estimated annual recurring revenue',
        dataFormat: 'currency',
        useWebSearch: true,
        credits: 3,
        example: '$5-10M ARR'
      },
      {
        name: 'Last Funding Date',
        description: 'Date of most recent funding round',
        dataFormat: 'date',
        useWebSearch: true,
        credits: 3,
        example: '2024-03-15'
      }
    ]
  },
  {
    category: 'Technology Stack',
    options: [
      {
        name: 'Primary Technology',
        description: 'Main programming language or framework',
        dataFormat: 'text',
        useWebSearch: false,
        credits: 1,
        example: 'React/Node.js'
      },
      {
        name: 'Cloud Provider',
        description: 'AWS, Google Cloud, Azure, etc.',
        dataFormat: 'text',
        useWebSearch: false,
        credits: 1,
        example: 'AWS'
      },
      {
        name: 'Has Public API',
        description: 'Whether company offers public API',
        dataFormat: 'boolean',
        useWebSearch: false,
        credits: 1,
        example: 'Yes'
      },
      {
        name: 'Mobile App Available',
        description: 'iOS/Android app availability',
        dataFormat: 'boolean',
        useWebSearch: false,
        credits: 1,
        example: 'Yes'
      }
    ]
  },
  {
    category: 'Contact Information',
    options: [
      {
        name: 'CEO/Founder Email',
        description: 'Primary decision maker contact',
        dataFormat: 'text',
        useWebSearch: true,
        credits: 3,
        example: 'john@company.com'
      },
      {
        name: 'LinkedIn Company Page',
        description: 'Official LinkedIn company profile',
        dataFormat: 'text',
        useWebSearch: true,
        credits: 3,
        example: 'linkedin.com/company/example'
      },
      {
        name: 'Support Email',
        description: 'Customer support contact',
        dataFormat: 'text',
        useWebSearch: false,
        credits: 1,
        example: 'support@company.com'
      },
      {
        name: 'Phone Number',
        description: 'Main business phone number',
        dataFormat: 'text',
        useWebSearch: false,
        credits: 1,
        example: '+1-555-123-4567'
      }
    ]
  }
];

export const mockCostEstimates = {
  baseAnalysis: {
    costPerWebsite: 0.0035,
    creditsPerWebsite: 1,
    description: 'Basic qualification analysis using GPT-4o'
  },
  enrichment: {
    contentExtraction: {
      costPerColumn: 0.0035,
      creditsPerColumn: 1,
      description: 'Extract data from website content'
    },
    webSearch: {
      costPerColumn: 0.0105,
      creditsPerColumn: 3,
      description: 'External web search for additional data'
    }
  },
  processingTime: {
    baseTimePerWebsite: 2, // seconds
    enrichmentTimePerColumn: 1, // additional seconds
    maxConcurrency: 10 // websites processed in parallel
  }
};

export const mockJobHistory = [
  {
    id: 'job_001',
    name: 'B2B SaaS Research - Q3',
    status: 'completed',
    created: '2025-09-20T10:30:00Z',
    completed: '2025-09-20T11:45:00Z',
    websites: {
      total: 500,
      processed: 498,
      qualified: 187,
      disqualified: 311
    },
    credits: {
      base: 500,
      enrichment: 1500,
      total: 2000
    },
    cost: '$7.00',
    template: 'B2B SaaS Companies'
  },
  {
    id: 'job_002', 
    name: 'E-commerce Prospects',
    status: 'running',
    created: '2025-09-22T14:15:00Z',
    progress: 0.65,
    websites: {
      total: 250,
      processed: 162,
      qualified: 89,
      disqualified: 73
    },
    credits: {
      estimated: 750,
      used: 487
    },
    cost: '$2.63',
    template: null
  }
];

export default {
  mockUploadedFile,
  mockTemplates,
  mockPreviewData,
  mockConversationalQuestions,
  mockEnrichmentOptions,
  mockCostEstimates,
  mockJobHistory
};